import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  QrCode,
  Download,
  Link,
  Palette,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Lock,
  Sparkles,
  Check
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Enter URL', icon: Link },
  { id: 2, title: 'Choose Style', icon: Palette },
];

const COLOR_PRESETS = [
  { fg: '#000000', bg: '#FFFFFF', name: 'Classic' },
  { fg: '#1a365d', bg: '#ebf8ff', name: 'Ocean' },
  { fg: '#744210', bg: '#fffff0', name: 'Amber' },
  { fg: '#22543d', bg: '#f0fff4', name: 'Forest' },
  { fg: '#702459', bg: '#fff5f7', name: 'Rose' },
  { fg: '#2d3748', bg: '#edf2f7', name: 'Slate' },
  { fg: '#9333ea', bg: '#faf5ff', name: 'Purple' },
];

const FreeQRCodeGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const qrRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [url, setUrl] = useState('');
  const [qrColor, setQrColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [qrSize, setQrSize] = useState(256);
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 2) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    } else {
      const isSwipeUp = distanceY > minSwipeDistance;
      const isSwipeDown = distanceY < -minSwipeDistance;
      
      if (isSwipeUp && !showPreview) {
        setShowPreview(true);
      } else if (isSwipeDown && showPreview) {
        setShowPreview(false);
      }
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !url.trim()) {
      toast({ title: 'URL Required', description: 'Please enter a URL', variant: 'destructive' });
      return;
    }
    if (currentStep < 2) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleDownload = () => {
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = 'qr-code.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast({ title: 'Downloaded!', description: 'QR code saved.' });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-1.5">
                Enter URL or Text <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="pl-10 h-12 text-base"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Enter any URL, text, or phone number to generate a QR code
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-medium text-gray-700 mb-3">
                <Palette className="w-4 h-4 inline mr-2" />
                Color Theme
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      setQrColor(preset.fg);
                      setBgColor(preset.bg);
                    }}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      qrColor === preset.fg && bgColor === preset.bg
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: preset.fg }} />
                      <div className="w-4 h-4 rounded border" style={{ backgroundColor: preset.bg }} />
                    </div>
                    <span className="text-xs font-medium">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-2 block">QR Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="flex-1 h-10 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-2 block">Background</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="flex-1 h-10 font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm mb-2 block">Size: {qrSize}px</Label>
              <input
                type="range"
                min="128"
                max="512"
                value={qrSize}
                onChange={(e) => setQrSize(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <SEO
        title="Free QR Code Generator | Create Custom QR Codes Online"
        description="Generate free QR codes instantly. Customize colors, download high-quality PNG files. No sign-up required to create."
        keywords="free QR code generator, QR code maker, custom QR code, QR code creator online free"
      />

      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <QrCode className="w-6 h-6 text-amber-500" />
              <span className="font-bold text-lg text-gray-900">QR Generator</span>
            </div>
            <button
              onClick={() => setShowPreview(true)}
              className="text-amber-600 font-medium text-sm"
            >
              Preview
            </button>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-lg mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-amber-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      currentStep > step.id ? 'bg-amber-500 text-white' :
                      currentStep === step.id ? 'bg-amber-100 text-amber-600 border-2 border-amber-500' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                    </div>
                    <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-12 sm:w-20 h-0.5 mx-2 ${currentStep > step.id ? 'bg-amber-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-lg mx-auto px-4 py-6 pb-32">
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {STEPS[currentStep - 1].title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {currentStep === 1 && "Enter the URL or text for your QR code"}
              {currentStep === 2 && "Customize the colors and style"}
            </p>
          </div>

          <Card className="shadow-sm border border-gray-100">
            <CardContent className="p-5">
              {renderStepContent()}
            </CardContent>
          </Card>
        </main>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-lg mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="flex-1 h-12">
                  <ChevronLeft className="w-5 h-5 mr-1" /> Back
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              
              <Button
                onClick={nextStep}
                className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                {currentStep === 2 ? (
                  <>Preview QR Code</>
                ) : (
                  <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
                )}
              </Button>
            </div>

            {/* Mobile Swipe-Up Preview Indicator */}
            <div 
              onClick={() => setShowPreview(true)}
              className="lg:hidden mt-6 mb-4 flex flex-col items-center cursor-pointer"
              data-testid="swipe-up-indicator"
            >
              <div className="flex flex-col items-center animate-bounce">
                <ChevronUp className="w-6 h-6 text-amber-500" />
                <ChevronUp className="w-6 h-6 text-amber-500 -mt-4" />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <QrCode className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-600">Swipe Up to Preview</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Swipe left/right to change steps</p>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent 
          className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-0"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <p className="text-xs text-center text-gray-400 mb-2">Swipe down to close</p>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center">Your QR Code</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            <div
              ref={qrRef}
              className="flex justify-center p-4 rounded-xl shadow-lg mb-4 mx-auto"
              style={{ backgroundColor: bgColor, width: 'fit-content' }}
            >
              <QRCodeCanvas
                value={url || 'https://pipercard.com'}
                size={qrSize > 280 ? 280 : qrSize}
                fgColor={qrColor}
                bgColor={bgColor}
                level="H"
                includeMargin={true}
              />
            </div>

            <Button
              onClick={handleDownload}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg"
            >
              {user ? (
                <><Download className="w-5 h-5 mr-2" /> Download QR Code</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Sign In to Download</>
              )}
            </Button>

            {showSignInPrompt && !user && (
              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-amber-800 font-medium mb-3">
                  Create a free account to download your QR code
                </p>
                <Button
                  onClick={() => navigate('/card-builder')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Sign Up Free
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FreeQRCodeGenerator;
